import { type NextRequest } from "next/server";

const NAVIGATION_DESTINATIONS = new Set(["document", "iframe", "frame", "embed", "object"]);

/**
 * Whether the request is a full (document) navigation, judged by the browser-set Fetch Metadata
 * headers (`Sec-Fetch-Dest` / `Sec-Fetch-Mode`). These are forbidden headers the browser attaches
 * to every request — scripts cannot spoof them, and unlike Next.js's own flight headers
 * (`next-router-prefetch`, `rsc`, ... — stripped before middleware runs, vercel/next.js#63728)
 * they do reach middleware.
 *
 * Router traffic (segment-cache prefetches, soft navigations, server actions) is issued via
 * fetch() and carries `Sec-Fetch-Dest: empty`, so it never counts as a navigation. Clients that
 * omit Fetch Metadata (curl, bots, Safari < 16.4) count as navigations so middleware never skips
 * work for them (fail open). Frame destinations count too: an embedded app boots like a document.
 */
export const isDocumentNavigation = (request: NextRequest): boolean => {
  const dest = request.headers.get("sec-fetch-dest");
  const mode = request.headers.get("sec-fetch-mode");
  if (dest === null || mode === null) {
    return true;
  }
  return mode === "navigate" && NAVIGATION_DESTINATIONS.has(dest);
};
